import FVector from '@data/geometry/FVector.js';
import { Vector2 } from 'three';

export type TriangleVertices = {
    A: FVector;
    B: FVector;
    C: FVector;
};

export function pointTriangleDistance(point: FVector, triangle: TriangleVertices) {
    // Get the vertices of the triangle
    const A = triangle.A;
    const B = triangle.B;
    const C = triangle.C;
    // Compute the normal vector of the plane (cross product of edge vectors)
    const AB = FVector.sub(B, A);
    const AC = FVector.sub(C, A);
    const normal = AB.cross(AC);

    // Compute the equation of the plane ax + by + cz + d = 0
    const d = -normal.dot(A);

    // Compute the distance between the point and the plane
    const distanceToPlane = Math.abs(normal.dot(point) + d) / normal.mag();
    // Project the point onto the plane
    const t = -(normal.dot(point) + d);
    const projectedPoint = FVector.add(point, FVector.mult(normal, t / normal.magSq()));

    // Check if the projected point is inside the triangle
    const insideTriangle = pointInsideTriangle(projectedPoint, triangle);

    if (insideTriangle) {
        return distanceToPlane;
    } else {
        // Compute the distance to each of the triangle's edges
        const distanceToAB = pointToLineSegmentDistance(point, A, B);
        const distanceToBC = pointToLineSegmentDistance(point, B, C);
        const distanceToCA = pointToLineSegmentDistance(point, C, A);

        // Return the minimum distance to the triangle's edges
        return Math.min(Math.min(distanceToAB, distanceToBC), distanceToCA);
    }
}

export function pointTrianglePerpendicularDistance(point: FVector, triangle: TriangleVertices) {
    const A = triangle.A;
    const B = triangle.B;
    const C = triangle.C;

    const AB = FVector.sub(B, A);
    const AC = FVector.sub(C, A);
    const normal = AB.cross(AC);
    const normalMagSq = normal.magSq();
    if (normalMagSq === 0) {
        return Infinity;
    }

    const d = -normal.dot(A);
    const distanceToPlane = Math.abs(normal.dot(point) + d) / Math.sqrt(normalMagSq);

    const t = -(normal.dot(point) + d);
    const projectedPoint = FVector.add(point, FVector.mult(normal, t / normalMagSq));

    const insideTriangle = pointInsideTriangle(projectedPoint, triangle);
    return insideTriangle ? distanceToPlane : Infinity;
}

function pointInsideTriangle(point: FVector, triangle: TriangleVertices) {
    const A = triangle.A;
    const B = triangle.B;
    const C = triangle.C;

    const v0 = FVector.sub(C, A);
    const v1 = FVector.sub(B, A);
    const v2 = FVector.sub(point, A);

    const dot00 = v0.dot(v0);
    const dot01 = v0.dot(v1);
    const dot02 = v0.dot(v2);
    const dot11 = v1.dot(v1);
    const dot12 = v1.dot(v2);

    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
    const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

    const epsilon = 1e-9;
    return u >= -epsilon && v >= -epsilon && u + v <= 1 + epsilon;
}

function pointToLineSegmentDistance(point: FVector, A: FVector, B: FVector) {
    const AB = FVector.sub(B, A);
    const AP = FVector.sub(point, A);
    // FVector BP = FVector.sub(point, B);

    const lengthSq = AB.magSq();
    if (lengthSq == 0) return AP.mag(); // A and B are the same point

    const t = Math.max(0, Math.min(1, AP.dot(AB) / lengthSq));
    const projection = FVector.add(A, FVector.mult(AB, t));

    // Calculate the distance between the point and the projection
    const distance = FVector.dist(point, projection);

    return distance;
}

/**
 * Check if a 2D point is inside a 2D polygon.
 *
 * @param {FVector} point - The point to check.
 * @param {FVector[]} polygon - Array of points defining the polygon.
 * @returns {boolean} True if the point is inside the polygon, false otherwise.
 */
type PointLike = { x: number; y: number };

const toPointLike = (point: FVector | Vector2): PointLike => ({ x: point.x, y: point.y });

export function isPointInPolygon(point: FVector, polygon: FVector[]): boolean {
    function direction(p1: FVector | Vector2, p2: FVector | Vector2, p3: FVector | Vector2) {
        const a = toPointLike(p1);
        const b = toPointLike(p2);
        const c = toPointLike(p3);

        const v1x = b.x - a.x;
        const v1y = b.y - a.y;
        const v2x = c.x - a.x;
        const v2y = c.y - a.y;

        // Cross product represent the following:
        // Positive Value: 	The triplet (p1,p2,p3) forms a counterclockwise turn.
        // Negative Value: 	The triplet forms a clockwise turn.
        // Zero: 			Points are collinear (lie on a straight line).
        return v1x * v2y - v1y * v2x;
    }

    function onSegment(p1: FVector | Vector2, p2: FVector | Vector2, p: FVector | Vector2) {
        const a = toPointLike(p1);
        const b = toPointLike(p2);
        const c = toPointLike(p);

        // Check if p lies within the bounding box of p1 and p2:
        let inBoundingBox =
            c.x >= Math.min(a.x, b.x) && c.x <= Math.max(a.x, b.x) && c.y >= Math.min(a.y, b.y) && c.y <= Math.max(a.y, b.y);

        // Check if the cross product is zero (collinear):
        let isCollinear = direction(p1, p2, p) == 0;

        // If on the same infinite line (collinear) and inside the bounding box (polygon border) then it's an intersection:
        return inBoundingBox && isCollinear;
    }

    function doLinesIntersect(p1: FVector | Vector2, p2: FVector | Vector2, p3: FVector | Vector2, p4: FVector | Vector2) {
        // Calculate the orientation of the points:
        const d1 = direction(p3, p4, p1); // Orientation of p1 with respect to Polygon border (p3-p4)
        const d2 = direction(p3, p4, p2); // Orientation of p2 with respect to Polygon border (p3-p4)
        const d3 = direction(p1, p2, p3); // Orientation of p3 with respect to Ray from outside to current Sensor position (p1-p2)
        const d4 = direction(p1, p2, p4); // Orientation of p4 with respect to Ray ... (p1-p2)

        // Check where the points of one segment are relative to the other segment:
        const p1LeftP2RightOfP3P4 = d1 > 0 && d2 < 0;
        const p1RightP2LeftOfP3P4 = d1 < 0 && d2 > 0;
        const p3LeftP4RightOfP1P2 = d3 > 0 && d4 < 0;
        const p3RightP4LeftOfP1P2 = d3 < 0 && d4 > 0;

        // Check that the points of one segment are in the opposite side of the other segment (intersection):
        const p1p2OppositeSidesOfP3P4 = p1LeftP2RightOfP3P4 || p1RightP2LeftOfP3P4;
        const p3p4OppositeSidesOfP1P2 = p3LeftP4RightOfP1P2 || p3RightP4LeftOfP1P2;

        if (p1p2OppositeSidesOfP3P4 && p3p4OppositeSidesOfP1P2) {
            return true;
        }

        // Check for special case in which point lies on segment:
        return (
            (d1 === 0 && onSegment(p3, p4, p1)) ||
            (d2 === 0 && onSegment(p3, p4, p2)) ||
            (d3 === 0 && onSegment(p1, p2, p3)) ||
            (d4 === 0 && onSegment(p1, p2, p4))
        );
    }

    // Check intersection of ray casted with every side of the Polygon:
    let outsidePoint = new Vector2(Number.MIN_SAFE_INTEGER, point.y);
    let intersections = 0;
    for (let i = 0; i < polygon.length; i++) {
        let start = polygon[i];
        let end = polygon[(i + 1) % polygon.length];

        if (doLinesIntersect(outsidePoint, point, start, end)) {
            intersections++;
        }
    }

    return intersections % 2 !== 0;
}

export function rayIntersectsSphere(ray: any, center: FVector, radius: number): boolean {
    const { start, direction } = ray;

    const oc = {
        x: start.x - center.x,
        y: start.y - center.y,
        z: start.z - center.z,
    };

    const a = direction.x * direction.x + direction.y * direction.y + direction.z * direction.z;
    const b = 2.0 * (oc.x * direction.x + oc.y * direction.y + oc.z * direction.z);
    const c = oc.x * oc.x + oc.y * oc.y + oc.z * oc.z - radius * radius;
    const discriminant = b * b - 4 * a * c;

    return discriminant > 0;
}

// Self Intersection check functions:
type V = { x: number; y: number };

export function orient(a: V, b: V, c: V) {
    const v = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
    return Math.sign(v);
}

export function onSegment(a: V, b: V, c: V) {
    return Math.min(a.x, c.x) <= b.x && b.x <= Math.max(a.x, c.x) && Math.min(a.y, c.y) <= b.y && b.y <= Math.max(a.y, c.y);
}

export function segmentsIntersect(p1: V, p2: V, p3: V, p4: V) {
    const o1 = orient(p1, p2, p3);
    const o2 = orient(p1, p2, p4);
    const o3 = orient(p3, p4, p1);
    const o4 = orient(p3, p4, p2);

    if (o1 !== o2 && o3 !== o4) return true; // general case

    // collinear cases
    if (o1 === 0 && onSegment(p1, p3, p2)) return true;
    if (o2 === 0 && onSegment(p1, p4, p2)) return true;
    if (o3 === 0 && onSegment(p3, p1, p4)) return true;
    if (o4 === 0 && onSegment(p3, p2, p4)) return true;

    return false;
}

export function pointInTriangle2D(point: V, a: V, b: V, c: V, eps = 1e-8) {
    const e0 = (point.x - a.x) * (b.y - a.y) - (point.y - a.y) * (b.x - a.x);
    const e1 = (point.x - b.x) * (c.y - b.y) - (point.y - b.y) * (c.x - b.x);
    const e2 = (point.x - c.x) * (a.y - c.y) - (point.y - c.y) * (a.x - c.x);
    const minE = Math.min(e0, e1, e2);
    const maxE = Math.max(e0, e1, e2);
    return !(minE < -eps && maxE > eps);
}

export function polygonHasSelfIntersection(pts: FVector[], ignoreClosing: boolean = true) {
    const n = pts.length;
    if (n < 4) return false;

    const P = pts.map((p) => ({ x: p.x, y: p.y }));

    // Number of segments to consider
    const segmentCount = ignoreClosing ? n - 1 : n;

    for (let i = 0; i < segmentCount; i++) {
        const i2 = ignoreClosing ? i + 1 : (i + 1) % n; // No wrap around if ignoreClosing.

        for (let j = i + 1; j < segmentCount; j++) {
            const j2 = ignoreClosing ? j + 1 : (j + 1) % n;

            // Skip same/adjacent edges
            if (i === j || i === j2 || i2 === j) continue;

            if (segmentsIntersect(P[i], P[i2], P[j], P[j2])) {
                return true;
            }
        }
    }

    return false;
}
